import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FiSearch, FiFilter, FiDownload, FiEye, FiEdit, FiTrash2, FiChevronDown, FiChevronRight, FiUsers } from "react-icons/fi";
import { sendApiRequest } from "../../utils";
import { toast } from "react-toastify";
import { CSVLink } from "react-csv";
import { format } from "date-fns";
import MLMTreeModal from "./MLMTreeModal";

const AllPartner = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [partnersPerPage] = useState(10);
  const [sortField, setSortField] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [showFilters, setShowFilters] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPartnerData, setSelectedPartnerData] = useState(null);
  const [expandedMLMTrees, setExpandedMLMTrees] = useState({});
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showMLMTreeModal, setShowMLMTreeModal] = useState(false);
  const [filters, setFilters] = useState({
    city: "",
    state: "",
    rank: "",
  });

  // Fetch partners data
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        setLoading(true);
        const response = await sendApiRequest("api/admin/partners", {}, "GET");
        if (response.status === 200) {
          setPartners(response.data.data);
          setFilteredPartners(response.data.data);
        } else {
          toast.error("Failed to fetch partners");
        }
      } catch (error) {
        console.error("Error fetching partners:", error);
        toast.error("Error fetching partners");
      } finally {
        setLoading(false);
      }
    };

    fetchPartners();
  }, []);

  // Filter and search partners
  useEffect(() => {
    let result = [...partners];

    // Apply search filter
    if (searchTerm) {
      result = result.filter(
        (partner) =>
          partner.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          partner.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          partner.phone.includes(searchTerm) ||
          partner.sponsorCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
          partner.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
          partner.state.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply filters
    if (filters.city) {
      result = result.filter(
        (partner) => partner.city.toLowerCase() === filters.city.toLowerCase()
      );
    }
    if (filters.state) {
      result = result.filter(
        (partner) => partner.state.toLowerCase() === filters.state.toLowerCase()
      );
    }
    if (filters.rank) {
      result = result.filter(
        (partner) => partner.rank.toLowerCase() === filters.rank.toLowerCase()
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      if (sortDirection === "asc") {
        return a[sortField] > b[sortField] ? 1 : -1;
      } else {
        return a[sortField] < b[sortField] ? 1 : -1;
      }
    });

    setFilteredPartners(result);
    setCurrentPage(1); // Reset to first page when filters change
  }, [partners, searchTerm, filters, sortField, sortDirection]);

  // Get unique cities, states, and ranks for filter dropdowns
  const uniqueCities = [...new Set(partners.map((partner) => partner.city))];
  const uniqueStates = [...new Set(partners.map((partner) => partner.state))];
  const uniqueRanks = [...new Set(partners.map((partner) => partner.rank))];

  // Get current partners for pagination
  const indexOfLastPartner = currentPage * partnersPerPage;
  const indexOfFirstPartner = indexOfLastPartner - partnersPerPage;
  const currentPartners = filteredPartners.slice(
    indexOfFirstPartner,
    indexOfLastPartner
  );

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      city: "",
      state: "",
      rank: "",
    });
    setSearchTerm("");
  };

  // Format date
  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Handle view partner details
  const handleViewPartner = async (partnerId) => {
    try {
      const response = await sendApiRequest(`api/admin/partners/${partnerId}`, {}, 'GET');
      if (response.data.success) {
        // Set the selected partner data for viewing
        setSelectedPartnerData(response.data.data);
        setShowViewModal(true);
        // You can implement a modal or navigation here to show the details
        toast.success('Partner details fetched successfully');
      } else {
        toast.error(response.data.message || 'Failed to fetch partner details');
      }
    } catch (error) {
      console.error('Error viewing partner:', error);
      toast.error('Error fetching partner details');
    }
  };

  // Handle edit partner
  const handleEditPartner = async (partner) => {
    setSelectedPartnerData(partner);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (formData) => {
    try {
      const response = await sendApiRequest(
        `api/admin/partners/${selectedPartnerData._id}`,
        formData,
        "PUT"
      );

      if (response.data.success) {
        toast.success("Partner updated successfully");
        // Update the partners list with the edited data
        setPartners(partners.map(partner =>
          partner._id === selectedPartnerData._id ? response.data.data : partner
        ));
        setShowEditModal(false);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error updating partner:", error);
      toast.error("Error updating partner");
    }
  };

  // Handle delete partner
  const handleDeletePartner = async (partnerId) => {
    if (window.confirm("Are you sure you want to delete this partner?")) {
      try {
        const response = await sendApiRequest(
          `api/admin/partners/${partnerId}`,
          {},
          "DELETE"
        );
        if (response.data.success) {
          toast.success("Partner deleted successfully");
          setPartners(partners.filter((partner) => partner._id !== partnerId));
        } else {
          toast.error(response.data.message);
        }
      } catch (error) {
        console.error("Error deleting partner:", error);
        toast.error("Error deleting partner");
      }
    }
  };

  // Toggle MLM tree expansion
  const toggleMLMTree = (partnerId) => {
    setExpandedMLMTrees(prev => ({
      ...prev,
      [partnerId]: !prev[partnerId]
    }));
  };

  // Handle view MLM tree
  const handleViewMLMTree = (partner) => {
    setSelectedPartner(partner);
    setShowMLMTreeModal(true);
  };

  const ViewModal = ({ partner, onClose }) => {
    if (!partner) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {partner?.fullName || 'No name provided'}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {partner?.sponsorCode || 'No sponsor code'} • {partner?.rank || 'No rank'}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex gap-6 mb-8">
              <div className="w-24 h-24 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center overflow-hidden">
                {partner?.profile_image ? (
                  <img src={partner.profile_image} alt={partner?.fullName || 'Profile'} className="w-full h-full object-cover" />
                ) : (
                  <svg className="w-12 h-12 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                )}
              </div>

              <div className="flex-1 grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Contact Information</label>
                    <p className="text-base text-gray-900 dark:text-white mt-1">
                      {partner?.email || 'No email provided'}
                    </p>
                    <p className="text-base text-gray-900 dark:text-white">
                      {partner?.phone || 'No phone provided'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Location</label>
                    <p className="text-base text-gray-900 dark:text-white mt-1">
                      {[partner?.city, partner?.state].filter(Boolean).join(', ') || 'No location provided'}
                    </p>
                    <p className="text-base text-gray-900 dark:text-white">
                      {partner?.country || 'No country provided'}
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Account Details</label>
                    <p className="text-base text-gray-900 dark:text-white mt-1">
                      Type: {partner?.account_type || 'Not specified'}
                    </p>
                    <p className="text-base text-gray-900 dark:text-white">
                      Username: {partner?.username || 'Not specified'}
                    </p>
                    <p className="text-base text-gray-900 dark:text-white">
                      Password: {partner?.password || 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Member Since</label>
                    <p className="text-base text-gray-900 dark:text-white mt-1">
                      {partner?.createdAt ? formatDate(partner.createdAt) : 'Date not available'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h3 className="font-semibold text-lg text-gray-800 dark:text-white mb-4">
                  Direct Referrals ({partner?.directReferrals?.length || 0})
                </h3>
                {partner?.directReferrals?.length > 0 ? (
                  <div className="space-y-3">
                    {partner.directReferrals.map(referral => (
                      <div key={referral?._id || Math.random()} className="flex items-center gap-3 p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors">
                        <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                          <svg className="w-5 h-5 text-gray-500 dark:text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {referral?.fullName || 'No name'}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {referral?.rank || 'No rank'} • {referral?.sponsorCode || 'No code'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No direct referrals found</p>
                )}
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h3 className="font-semibold text-lg text-gray-800 dark:text-white mb-4">MLM Network</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 rounded-full bg-blue-500"></span>
                    <p className="text-gray-900 dark:text-white">
                      Level: {(partner?.mlmTree?.depth ?? -1) + 1}
                    </p>
                  </div>
                  {partner?.mlmTree?.parent ? (
                    <div className="flex items-center gap-2">
                      <span className="w-4 h-4 rounded-full bg-green-500"></span>
                      <p className="text-gray-900 dark:text-white">Has upline sponsor</p>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="w-4 h-4 rounded-full bg-yellow-500"></span>
                      <p className="text-gray-900 dark:text-white">Top level sponsor</p>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 rounded-full bg-purple-500"></span>
                    <p className="text-gray-900 dark:text-white">
                      Team Size: {partner?.mlmTree?.children?.length || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-lg text-gray-800 dark:text-white mb-4">
                Purchased Packages ({partner?.purchasedPackages?.length || 0})
              </h3>
              {partner?.purchasedPackages?.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {partner.purchasedPackages.map(pkg => (
                    <div key={pkg?._id || Math.random()} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                      <p className="font-medium text-gray-900 dark:text-white">
                        {pkg?.name || 'Unnamed package'}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Purchased on: {pkg?.purchaseDate ? formatDate(pkg.purchaseDate) : 'Date not available'}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-sm">No packages purchased yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };


  const EditModal = ({ partner, onClose, onSubmit }) => {
    const [formData, setFormData] = useState({
      fullName: partner?.fullName || '',
      email: partner?.email || '',
      phone: partner?.phone || '',
      city: partner?.city || '',
      state: partner?.state || '',
      rank: partner?.rank || '',
      password: partner?.password || '',
    });

    const [errors, setErrors] = useState({
      fullName: false,
      email: false,
      phone: false,
    });



    const AllPartnerStages = [
      { name: 'Director', value: '25%', income: 300000000 },
      { name: 'Office Head', value: '22%', income: 150000000 },  // Recommended
      { name: 'Z.M', value: '20%', income: 100000000 },
      { name: 'A.Z.M', value: '18%', income: 75000000 },
      { name: 'A.M', value: '16%', income: 50000000 },
      { name: 'A.Area.M', value: '14%', income: 25000000 },
      { name: 'S.M', value: '12%', income: 10000000 },
      { name: 'A.S.M', value: '10%', income: 4000000 },
      { name: 'Sr. Associate', value: '7%', income: 1500000 },
      { name: 'Associate', value: '5%', income: 500000 },
    ];

    const validateForm = () => {
      const newErrors = {
        fullName: !formData.fullName.trim(),
        email: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email),
        phone: !formData.phone.trim(),
      };
      setErrors(newErrors);
      return !Object.values(newErrors).some(error => error);
    };

    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: false }));
      }
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      if (validateForm()) {
        onSubmit(formData);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 transition-opacity duration-300">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-2xl w-full transform transition-all duration-300 scale-100 hover:scale-[1.01] backdrop-blur-lg">
          <form onSubmit={handleSubmit} className="p-8">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">Edit Partner</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {partner?.sponsorCode || 'No sponsor code'}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-full text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-6">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    className={`block w-full rounded-lg border-2 py-3 px-4 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${errors.fullName ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'
                      }`}
                    placeholder="Enter full name"
                  />
                  {errors.fullName && (
                    <p className="mt-2 text-sm text-red-600">Full name is required</p>
                  )}
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`block w-full rounded-lg border-2 py-3 px-4 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${errors.email ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'
                      }`}
                    placeholder="Enter email"
                  />
                  {errors.email && (
                    <p className="mt-2 text-sm text-red-600">Please enter a valid email</p>
                  )}
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`block w-full rounded-lg border-2 py-3 px-4 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${errors.phone ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'
                      }`}
                    placeholder="Enter phone number"
                  />
                  {errors.phone && (
                    <p className="mt-2 text-sm text-red-600">Phone number is required</p>
                  )}
                </div>
              </div>

              <div className="space-y-6">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="block w-full rounded-lg border-2 py-3 px-4 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 border-gray-200 dark:border-gray-700"
                    placeholder="Enter city"
                  />
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    State
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className="block w-full rounded-lg border-2 py-3 px-4 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 border-gray-200 dark:border-gray-700"
                    placeholder="Enter state"
                  />
                </div>
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Password
                  </label>
                  <input
                    type="text"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="block w-full rounded-lg border-2 py-3 px-4 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 border-gray-200 dark:border-gray-700"
                    placeholder="Enter password"
                  />
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Rank
                  </label>
                  <select
                    name="rank"
                    value={formData.rank}
                    onChange={handleChange}
                    className="block w-full rounded-lg border-2 py-3 px-4 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 border-gray-200 dark:border-gray-700"
                  >
                    <option value="">Select a rank</option>
                    {AllPartnerStages.map((stage) => (
                      <option key={stage.name} value={stage.name}>
                        {stage.name} ({stage.value})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="mt-10 pt-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-3 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 focus:ring-4 focus:ring-blue-300 focus:ring-opacity-50 transition-all duration-200 font-medium"
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };


  // Format MLM tree information for the table
  // Format partner data for CSV export
  const formatPartnersForCSV = (partners) => {
    // Return an array of objects with the same structure as our data
    return partners.map(partner => ({
      "Full Name": partner.fullName,
      "Email": partner.email,
      "Phone": partner.phone,
      "Sponsor Code": partner.sponsorCode,
      "City": partner.city,
      "State": partner.state,
      "Rank": partner.rank,
      "Created At": format(new Date(partner.createdAt), 'yyyy-MM-dd HH:mm:ss'),
      "MLM Depth": partner.mlmTree?.depth || 0,
      "MLM Children": partner.mlmTree?.children?.length || 0
    }));
  };

  const formatMLMInfo = (partner) => {
    if (!partner.mlmTree) {
      return (
        <div className="text-xs text-gray-500 dark:text-gray-400 italic">
          No MLM data available
        </div>
      );
    }

    const { children, depth } = partner.mlmTree;

    return (
      <div className="text-xs">
        <div className="flex items-center">
          <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
          <span className="font-medium text-blue-600 dark:text-blue-400">MLM Network</span>
          <span className="ml-2 px-1.5 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            Depth: {depth}
          </span>
          {children && children.length > 0 && (
            <span className="ml-2 px-1.5 py-0.5 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
              {children.length} children
            </span>
          )}
        </div>
        <button
          onClick={() => handleViewMLMTree(partner)}
          className="mt-1 text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center"
        >
          <FiUsers className="mr-1" />
          View MLM Tree
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
          All Partners
        </h1>
        <div className="mt-4 md:mt-0 flex space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
          >
            <FiFilter className="mr-2" />
            Filters
          </button>
          <CSVLink
            data={formatPartnersForCSV(filteredPartners)}
            filename={`partners-export-${format(new Date(), 'yyyy-MM-dd')}.csv`}
            className="flex items-center px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors cursor-pointer"
          >
            <FiDownload className="mr-2" />
            Export
          </CSVLink>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 border border-gray-100 dark:border-gray-700">
        <div className="flex flex-col md:flex-row md:items-center md:space-x-4">
          <div className="relative flex-1 mb-4 md:mb-0">
            <input
              type="text"
              placeholder="Search partners..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <FiSearch className="absolute left-3 top-3 text-gray-400" />
          </div>

          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="flex flex-col md:flex-row md:items-center md:space-x-4 mt-4 md:mt-0"
            >
              <select
                name="city"
                value={filters.city}
                onChange={handleFilterChange}
                className="mb-4 md:mb-0 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Cities</option>
                {uniqueCities.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>

              <select
                name="state"
                value={filters.state}
                onChange={handleFilterChange}
                className="mb-4 md:mb-0 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All States</option>
                {uniqueStates.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>

              <select
                name="rank"
                value={filters.rank}
                onChange={handleFilterChange}
                className="mb-4 md:mb-0 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Ranks</option>
                {uniqueRanks.map((rank) => (
                  <option key={rank} value={rank}>
                    {rank}
                  </option>
                ))}
              </select>

              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Clear Filters
              </button>
            </motion.div>
          )}
        </div>
      </div>

      {/* Partners Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden border border-gray-100 dark:border-gray-700">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Loading partners...</p>
          </div>
        ) : filteredPartners.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-600 dark:text-gray-400">No partners found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort("fullName")}
                    >
                      Name
                      {sortField === "fullName" && (
                        <span className="ml-1">
                          {sortDirection === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort("email")}
                    >
                      Email
                      {sortField === "email" && (
                        <span className="ml-1">
                          {sortDirection === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort("phone")}
                    >
                      Phone
                      {sortField === "phone" && (
                        <span className="ml-1">
                          {sortDirection === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort("sponsorCode")}
                    >
                      Sponsor Code
                      {sortField === "sponsorCode" && (
                        <span className="ml-1">
                          {sortDirection === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort("rank")}
                    >
                      Rank
                      {sortField === "rank" && (
                        <span className="ml-1">
                          {sortDirection === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort("city")}
                    >
                      City
                      {sortField === "city" && (
                        <span className="ml-1">
                          {sortDirection === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort("state")}
                    >
                      State
                      {sortField === "state" && (
                        <span className="ml-1">
                          {sortDirection === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort("createdAt")}
                    >
                      Joined
                      {sortField === "createdAt" && (
                        <span className="ml-1">
                          {sortDirection === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                    >
                      MLM Tree
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {currentPartners.map((partner) => (
                    <tr
                      key={partner._id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-r from-sky-500 to-blue-500 flex items-center justify-center text-white font-bold">
                              {partner.fullName.charAt(0)}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {partner.fullName}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {partner.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {partner.phone}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {partner.sponsorCode}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {partner.rank}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {partner.city}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {partner.state}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {formatDate(partner.createdAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {formatMLMInfo(partner)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleViewPartner(partner._id)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                            title="View Details"
                          >
                            <FiEye className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleEditPartner(partner)}
                            className="text-emerald-600 hover:text-emerald-900 dark:text-emerald-400 dark:hover:text-emerald-300"
                            title="Edit Partner"
                          >
                            <FiEdit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeletePartner(partner._id)}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            title="Delete Partner"
                          >
                            <FiTrash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={
                    currentPage ===
                    Math.ceil(filteredPartners.length / partnersPerPage)
                  }
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Showing{" "}
                    <span className="font-medium">
                      {indexOfFirstPartner + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-medium">
                      {Math.min(
                        indexOfLastPartner,
                        filteredPartners.length
                      )}
                    </span>{" "}
                    of{" "}
                    <span className="font-medium">{filteredPartners.length}</span>{" "}
                    results
                  </p>
                </div>
                <div>
                  <nav
                    className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                    aria-label="Pagination"
                  >
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <svg
                        className="h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                    {Array.from(
                      {
                        length: Math.ceil(
                          filteredPartners.length / partnersPerPage
                        ),
                      },
                      (_, i) => i + 1
                    )
                      .filter(
                        (page) =>
                          page === 1 ||
                          page ===
                          Math.ceil(
                            filteredPartners.length / partnersPerPage
                          ) ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                      )
                      .map((page, index, array) => {
                        // Add ellipsis for skipped pages
                        if (
                          index > 0 &&
                          array[index - 1] !== page - 1
                        ) {
                          return (
                            <React.Fragment key={`ellipsis-${page}`}>
                              <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-300">
                                ...
                              </span>
                              <button
                                onClick={() => paginate(page)}
                                className={`relative inline-flex items-center px-4 py-2 border ${currentPage === page
                                  ? "z-10 bg-blue-50 dark:bg-blue-900 border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-300"
                                  : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                                  } text-sm font-medium`}
                              >
                                {page}
                              </button>
                            </React.Fragment>
                          );
                        }
                        return (
                          <button
                            key={page}
                            onClick={() => paginate(page)}
                            className={`relative inline-flex items-center px-4 py-2 border ${currentPage === page
                              ? "z-10 bg-blue-50 dark:bg-blue-900 border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-300"
                              : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                              } text-sm font-medium`}
                          >
                            {page}
                          </button>
                        );
                      })}
                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={
                        currentPage ===
                        Math.ceil(filteredPartners.length / partnersPerPage)
                      }
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <svg
                        className="h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {showViewModal && (
        <ViewModal
          partner={selectedPartnerData}
          onClose={() => {
            setShowViewModal(false);
            setSelectedPartnerData(null);
          }}
        />
      )}

      {showEditModal && (
        <EditModal
          partner={selectedPartnerData}
          onClose={() => {
            setShowEditModal(false);
            setSelectedPartnerData(null);
          }}
          onSubmit={handleEditSubmit}
        />
      )}

      {/* MLM Tree Modal */}
      <MLMTreeModal
        isOpen={showMLMTreeModal}
        onClose={() => setShowMLMTreeModal(false)}
        partner={selectedPartner}
      />
    </div>
  );
};

export default AllPartner;
